import { ChannelType } from 'common/core/constants/ChannelType'
import { createParseField, parseAvailableFields } from 'common/fields/api/adapter'
import { BackendAvailableFieldsMap, BackendFieldData } from 'common/fields/api/types'
import { FilterScenario } from 'common/filter/constants'
import { Body, McAPI } from 'utils/api/types'
import {
  Handler,
  NotificationsListener,
} from 'utils/services/notificationsService/notificationsServiceTypes'

import { FieldGroupId } from '../entities/enums'
import { Field } from '../entities/field'

import { IFieldsAPI } from './iFieldsAPI'

const availableFieldsURL = '/:currentAccountID/subscribers/availableFields?allow_user_id=true'

const createFieldUrl = {
  [FieldGroupId.USER]: '/:currentAccountID/customFields/create',
  [FieldGroupId.BOT]: '/:currentAccountID/globalFields/create',
}

const CUF_WS_EVENTS = [
  'cuf_created',
  'cuf_updated',
  'cuf_archived',
  'cuf_restored',
  'cuf_deleted',
  'cufs_moved',
]
const CUF_BULK_WS_EVENTS = ['cufs_moved_bulk', 'cufs_archived', 'cufs_deleted', 'cufs_created']
const GAF_WS_EVENTS = [
  'global_field_created',
  'global_field_updated',
  'global_field_archived',
  'global_field_restored',
  'global_field_deleted',
]
const GAF_BULK_WS_EVENTS = ['global_fields_archived', 'global_fields_deleted']

type AvailableFieldsResponse = Body & BackendAvailableFieldsMap

type CreateFieldResponse = Body & {
  field: SafeUnknownObject
}

type UpdateListener = (field: Field) => void

export default class FieldsAPI implements IFieldsAPI {
  private api: McAPI
  private updateListener?: UpdateListener
  private notificationsListener: NotificationsListener
  private isDisposed = false
  private fixSystemFields: boolean

  constructor(
    mcAPI: McAPI,
    accountNotificationsListener: NotificationsListener,
    fixSystemFields = true,
  ) {
    this.api = mcAPI
    this.notificationsListener = accountNotificationsListener
    this.fixSystemFields = fixSystemFields

    accountNotificationsListener.on(CUF_WS_EVENTS, this.handleUserFieldUpdate)
    accountNotificationsListener.on(CUF_BULK_WS_EVENTS, this.handleUserFieldsUpdate)
    accountNotificationsListener.on(GAF_WS_EVENTS, this.handleBotFieldUpdate)
    accountNotificationsListener.on(GAF_BULK_WS_EVENTS, this.handleBotFieldsUpdate)
  }

  /**
   * Permanently release owned subscriptions and the update listener. Later listener attachments
   * are ignored; pending requests still resolve/reject normally without emitting updates.
   * Safe during notification delivery: disposal cannot skip another subscriber.
   */
  dispose = () => {
    if (this.isDisposed) {
      return
    }

    this.isDisposed = true
    this.updateListener = undefined
    this.notificationsListener.removeListener(CUF_WS_EVENTS, this.handleUserFieldUpdate)
    this.notificationsListener.removeListener(CUF_BULK_WS_EVENTS, this.handleUserFieldsUpdate)
    this.notificationsListener.removeListener(GAF_WS_EVENTS, this.handleBotFieldUpdate)
    this.notificationsListener.removeListener(GAF_BULK_WS_EVENTS, this.handleBotFieldsUpdate)
  }

  private handleUserFieldUpdate: Handler = ({ model }) => {
    this.emitUpdate(FieldGroupId.USER, model)
  }

  private handleUserFieldsUpdate: Handler = ({ models }) => {
    models.forEach((model: SafeUnknownObject) => this.emitUpdate(FieldGroupId.USER, model))
  }

  private handleBotFieldUpdate: Handler = ({ model }) => {
    this.emitUpdate(FieldGroupId.BOT, model)
  }

  private handleBotFieldsUpdate: Handler = ({ models }) => {
    models.forEach((model: SafeUnknownObject) => this.emitUpdate(FieldGroupId.BOT, model))
  }

  getAvailableFields = async (
    connectedChannels: ChannelType[],
    scenario?: FilterScenario,
  ): Promise<Field[]> => {
    let url = availableFieldsURL

    if (scenario) {
      url += `&scenario=${scenario}`
    }

    const response = await this.api.get<AvailableFieldsResponse>(url)
    const parsedFields = parseAvailableFields(response, this.fixSystemFields)

    return filterFieldsWithConnectedChannels(parsedFields, connectedChannels)
  }

  createUserField = (fieldData: BackendFieldData): Promise<Field> => {
    return this.createField(fieldData, FieldGroupId.USER)
  }

  createBotField = (fieldData: BackendFieldData): Promise<Field> => {
    return this.createField(fieldData, FieldGroupId.BOT)
  }

  addUpdateListener = (listener: UpdateListener) => {
    if (!this.isDisposed) {
      this.updateListener = listener
    }
  }

  private createField = async (
    body: BackendFieldData,
    group: FieldGroupId.USER | FieldGroupId.BOT,
  ) => {
    const { field } = await this.api.post<CreateFieldResponse>(createFieldUrl[group], { body })
    this.emitUpdate(group, field)
    return this.parseField(group, field)
  }

  private parseField = (groupId: FieldGroupId, model: SafeUnknownObject) => {
    const rawField = { ...model, name: `${groupId}_${model.field_id}` }
    return createParseField(groupId)(rawField)
  }

  private emitUpdate = (groupId: FieldGroupId, model: SafeUnknownObject) => {
    this.updateListener?.(this.parseField(groupId, model))
  }
}

const filterFieldsWithConnectedChannels = (fields: Field[], connectedChannels: ChannelType[]) =>
  fields.filter(({ channel }: Field) => (channel ? connectedChannels.includes(channel) : true))
